import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Text, TextType } from 'ui-components/components/text';

import { EnhancedReturnsPanel } from './enhanced-returns-panel';
import { ReturnsTabContextProvider } from './hooks/use-returns-tab-context';
import messages from './messages';
import { ReturnsPromotionalBanner } from './returns-promotional-banner';
import { ShipstationReturnsPanel } from './shipstation-returns-panel/shipstation-returns-panel';
import { StandardReturnsPanel } from './standard-returns-panel';
import style from './style.module.less';
import getDisplayInformation from '../../../../../components/data-driven-logo/get-display-information';
import { Features } from '../../../../../constants/features';
import { emptyObject } from 'ui-components/default-props/empty';
import { useLegacyBrandedReturnsFeatureGate } from '../../../../../feature-gates/use-legacy-branded-returns-feature-gate';
import { useShowStandardReturnsPortalForEnhancedStoreFeatureGate } from '../../../../../feature-gates/use-standard-returns-for-shopify-store-feature-gate';
import Global from '../../../../../store/global-selectors';
import { useIsEnhancedReturnsFeatureEnabled } from '../../../../../store/returns/returns.helpers';
import { useIsFeatureIncludedInAnyBillingPlan } from '../../../payment-and-subscription/hooks/use-plan-details';
import { useIsShipstationReturnsEnabled } from '../../../../../hooks/use-seller-setting';

/** @typedef {import("../types").ReturnSettings} ReturnSettings */

/**
 * Returns Tab component that handles returns settings for a store
 * @param {object} props - Component props
 * @param {ReturnSettings} props.returnSettings - The return settings
 * @returns {JSX.Element} The ReturnsTab component
 */
const ReturnsTab = ({ returnSettings }) => {
  const { storeGuid } = useParams();
  const store = useSelector((state) => Global.selectSellerStoreData(state, { storeGuid })) || emptyObject;
  const { name: marketplaceName } = getDisplayInformation({ marketplaceId: store.marketplaceId });
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const supportsEnhancedReturns = store?.supportsEnhancedReturns;
  const isEnhancedReturnsFeatureEnabled = useIsEnhancedReturnsFeatureEnabled();
  const isEnhancedReturnsAvailableOnAnyPlan = useIsFeatureIncludedInAnyBillingPlan(Features.EnhancedReturns);
  const isLegacyBrandedReturnsFeatureAvailableOnAnyPlan = useIsFeatureIncludedInAnyBillingPlan(Features.LegacyBrandedReturns);
  const isStoreSetupCompleteForEnhancedReturns = store?.isStoreSetupCompleteForEnhancedReturns;
  const showStandardReturnPortalConfigForEnhancedStore = useShowStandardReturnsPortalForEnhancedStoreFeatureGate();

  const { useBrandedReturns } = returnSettings;
  const { isFeatureEnabled: hasLegacyBrandedReturns } = useLegacyBrandedReturnsFeatureGate();
  const showEnhancedReturnsContent = supportsEnhancedReturns && isEnhancedReturnsFeatureEnabled;
  return (
    <ReturnsTabContextProvider
      value={{
        hasLegacyBrandedReturns,
        isEnhancedReturnsAvailableOnAnyPlan,
        isEnhancedReturnsFeatureEnabled,
        isLegacyBrandedReturnsFeatureAvailableOnAnyPlan,
        isStoreSetupCompleteForEnhancedReturns,
        isShipstationReturnsEnabled,
        marketplaceName,
        showStandardReturnPortalConfigForEnhancedStore,
        storeGuid,
        supportsEnhancedReturns,
        useBrandedReturns,
      }}
    >
      {!isShipstationReturnsEnabled && (
        <div className={style['return-settings']}>
          <Text
            textType={TextType.H2}
            value={showEnhancedReturnsContent ? messages.returnsAndExchangesSettingsTitle : messages.returnsSettingsTitle}
          />
          <Text
            className={style['standard-returns-description']}
            textType={TextType.Paragraph}
            value={showEnhancedReturnsContent ? messages.returnsAndExchangesSettingsDescription : messages.returnSettingsDescription}
          />
        </div>
      )}
      <ReturnsPromotionalBanner />
      <EnhancedReturnsPanel />
      <StandardReturnsPanel returnSettings={returnSettings} />
      <ShipstationReturnsPanel returnSettings={returnSettings} />
    </ReturnsTabContextProvider>
  );
};

export default ReturnsTab;
